import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface GuardrailEntry {
  id: string;
  title: string;
  description: string;
  category: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  pattern: string;
  fix: string;
  fitnessTest: string;
  createdAt: string;
  updatedAt: string;
}

export interface AnalysisResult {
  entries: Omit<GuardrailEntry, 'id' | 'createdAt' | 'updatedAt'>[];
  summary: string;
}

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiChatResponse {
  reply: string;
}

export interface CiSettings {
  configured: boolean;
  owner?: string;
  repo?: string;
  installationId?: number;
}

export interface PublishToCiResult {
  published: boolean;
  prUrl: string;
  prNumber: number;
  scriptsCreated: number;
  scriptsRemoved: number;
  /** False when the GitHub App installation lacks the Workflows permission. */
  workflowCommitted?: boolean;
}

export const guardrailsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async listEntries(): Promise<GuardrailEntry[]> {
    const { data } = await api.get('/guardrails');
    return data;
  },

  async getEntry(id: string): Promise<GuardrailEntry> {
    const { data } = await api.get(`/guardrails/${id}`);
    return data;
  },

  async analyzeIssues(text: string): Promise<AnalysisResult> {
    // Start async job
    const { data: job } = await api.post('/guardrails/analyze', { text });
    const jobId = job.jobId;

    // Poll until complete (max ~2 minutes)
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: status } = await api.get(`/guardrails/jobs/${jobId}`);
      if (status.status === 'completed') return status.result;
      if (status.status === 'failed') throw new Error(status.error || 'Analysis failed');
    }
    throw new Error('Analysis timed out');
  },

  async saveEntries(entries: Omit<GuardrailEntry, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<GuardrailEntry[]> {
    const { data } = await api.post('/guardrails', { entries });
    return data;
  },

  async updateEntry(id: string, data: Partial<Omit<GuardrailEntry, 'id' | 'createdAt' | 'updatedAt'>>): Promise<GuardrailEntry> {
    const { data: updated } = await api.put(`/guardrails/${id}`, data);
    return updated;
  },

  async deleteEntry(id: string): Promise<void> {
    await api.delete(`/guardrails/${id}`);
  },

  async chat(messages: AiChatMessage[], repo?: string): Promise<AiChatResponse> {
    const { data } = await api.post('/guardrails/chat', { messages, repo });
    return data;
  },

  async getCiSettings(): Promise<CiSettings> {
    const { data } = await api.get('/guardrails/ci-settings');
    return data;
  },

  async saveCiSettings(settings: { owner: string; repo: string; installationId: number }): Promise<CiSettings> {
    const { data } = await api.put('/guardrails/ci-settings', settings);
    return data;
  },

  async startPublishToCi(): Promise<string> {
    const { data } = await api.post('/guardrails/publish-to-ci');
    return data.jobId;
  },

  async getJob(jobId: string): Promise<{ status: string; result?: PublishToCiResult; error?: string }> {
    const { data } = await api.get(`/guardrails/jobs/${jobId}`);
    return data;
  },

  async getPublishedScripts(): Promise<Record<string, string>> {
    const { data } = await api.get('/guardrails/published-scripts');
    return data.scripts || {};
  },

  async publishSingleToCi(id: string): Promise<string> {
    const { data } = await api.post(`/guardrails/${id}/publish-to-ci`);
    return data.jobId;
  },
};
